import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Plane, Clock, ArrowRight, Filter, ChevronDown, ChevronUp, Tag, MapPin, Calendar, Users, ArrowRightLeft, Search, ArrowUpDown, Zap, Timer, X, Luggage, Utensils, Plus, Minus, AlertCircle } from 'lucide-react';
import { airports } from '../data/mock';
import { fetchFareRulesForFlight } from '../utils/fareRulesRouter';
import { buildSelectedFlightPayload } from '../utils/fareSelection';
import { useAgentAuth } from '../context/AgentAuthContext';
import Footer from '../components/Footer';
import BottomSheet from '../components/BottomSheet';
import PullToRefresh from '../components/PullToRefresh';
import ComboFlightCard from '../components/ComboFlightCard';
import FilterSidebar from '../components/FilterSidebar';
import MobileFilterSheet from '../components/MobileFilterSheet';
import MobileModifySearch from '../components/MobileModifySearch';
import MobileHeader from '../components/MobileHeader';
import DesktopModifySearch from '../components/DesktopModifySearch';
import SortButtons from '../components/SortButtons';
import { fetchAirlineLogos } from '../utils/airlineLogoCache';
import useInfiniteScroll from '../hooks/useInfiniteScroll';
import axios from 'axios';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { rememberCurrentSearchUrl } from '../utils/lastSearchUrl';
import { tripBaggageBySegmentFromPriceInfo } from '../utils/tripjackTripBaggage';
import { publicFareTypeLabel } from '../utils/acfFareDisplay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to get refundability badge info
const getRefundabilityInfo = (refundableType) => {
  switch (refundableType) {
    case 1:
      return {
        label: 'Refundable',
        bgColor: 'bg-green-50',
        textColor: 'text-green-700',
        borderColor: 'border-green-200'
      };
    case 2:
      return {
        label: 'Partially Refundable',
        bgColor: 'bg-yellow-50',
        textColor: 'text-yellow-700',
        borderColor: 'border-yellow-200'
      };
    case 0:
    default:
      return {
        label: 'Non-Refundable',
        bgColor: 'bg-red-50',
        textColor: 'text-red-700',
        borderColor: 'border-red-200'
      };
  }
};

// Skeleton Loader Component for better perceived performance
const FlightSkeleton = () => (
  <div className="bg-white rounded-lg p-4 mb-4 animate-pulse">
    <div className="flex justify-between items-start mb-3">
      <div className="flex-1">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      </div>
      <div className="h-8 bg-gray-200 rounded w-24"></div>
    </div>
    <div className="flex justify-between items-center mb-3">
      <div className="h-10 bg-gray-200 rounded w-20"></div>
      <div className="flex-1 mx-4">
        <div className="h-4 bg-gray-200 rounded w-full"></div>
      </div>
      <div className="h-10 bg-gray-200 rounded w-20"></div>
    </div>
    <div className="flex justify-between items-center">
      <div className="h-4 bg-gray-200 rounded w-32"></div>
      <div className="h-10 bg-teal-100 rounded w-32"></div>
    </div>
  </div>
);

const InternationalRoundTripResults = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { agent: agentSession } = useAgentAuth();
  
  // Search State
  const [showModifySearch, setShowModifySearch] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showTimingSheet, setShowTimingSheet] = useState(false);
  const [showSortSheet, setShowSortSheet] = useState(false);
  const [tripType, setTripType] = useState('roundTrip');
  const [fromAirport, setFromAirport] = useState(searchParams.get('from') || 'BOM');
  const [toAirport, setToAirport] = useState(searchParams.get('to') || 'DXB');
  
  const getDefaultDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date.toISOString().split('T')[0];
  };
  
  const [departureDate, setDepartureDate] = useState(searchParams.get('date') || getDefaultDate());
  const [returnDate, setReturnDate] = useState(searchParams.get('return') || '');
  const [adults, setAdults] = useState(Number(searchParams.get('adults')) || 1);
  const [children, setChildren] = useState(Number(searchParams.get('children')) || 0);
  const [infants, setInfants] = useState(Number(searchParams.get('infants')) || 0);
  const [travelClass, setTravelClass] = useState(searchParams.get('class') || 'Economy');

  // Sync state with URL params whenever they change
  useEffect(() => {
    const newFrom = searchParams.get('from') || 'BOM';
    const newTo = searchParams.get('to') || 'DXB';
    const newDate = searchParams.get('date') || getDefaultDate();
    const newReturn = searchParams.get('return') || '';
    const newAdults = Number(searchParams.get('adults')) || 1;
    const newChildren = Number(searchParams.get('children')) || 0;
    const newInfants = Number(searchParams.get('infants')) || 0;
    const newClass = searchParams.get('class') || 'Economy';
    
    // Only update if values actually changed
    if (newFrom !== fromAirport) setFromAirport(newFrom);
    if (newTo !== toAirport) setToAirport(newTo);
    if (newDate !== departureDate) setDepartureDate(newDate);
    if (newReturn !== returnDate) setReturnDate(newReturn);
    if (newAdults !== adults) setAdults(newAdults);
    if (newChildren !== children) setChildren(newChildren);
    if (newInfants !== infants) setInfants(newInfants);
    if (newClass !== travelClass) setTravelClass(newClass);
  }, [searchParams]);

  useEffect(() => {
    rememberCurrentSearchUrl();
  }, [location.pathname, location.search]);

  // Flight data
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [supplierErrors, setSupplierErrors] = useState({});

  // Pagination state for performance optimization
  const [displayCount, setDisplayCount] = useState(20);
  const ITEMS_PER_PAGE = 20;

  const fromCode = fromAirport;
  const toCode = toAirport;

  const [selectedStops, setSelectedStops] = useState([]);
  const [selectedAirlines, setSelectedAirlines] = useState([]);
  const [selectedDepartureTimes, setSelectedDepartureTimes] = useState([]);
  const [selectedArrivalTimes, setSelectedArrivalTimes] = useState([]);
  const [activeTab, setActiveTab] = useState({});
  const [fareRules, setFareRules] = useState({});
  const [loadingFareRules, setLoadingFareRules] = useState({});
  const [priceRange, setPriceRange] = useState([0, 300000]);
  const [sortBy, setSortBy] = useState('price');
  const [expandedFlight, setExpandedFlight] = useState(null);
  const [airlineLogos, setAirlineLogos] = useState({});
  const [selectedFares, setSelectedFares] = useState({}); // Track selected fare per flight

  // Fetch flights on mount
  useEffect(() => {
    fetchFlights();
  }, [fromAirport, toAirport, departureDate, returnDate, adults, children, infants, travelClass, searchParams]);

  const fetchFlights = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching international round-trip flights...', {
        origin: fromAirport,
        destination: toAirport,
        departure_date: departureDate,
        return_date: returnDate
      });

      // Sprint-6 · Forward industry-form params from URL (B3/B4/B5)
      const _pft = searchParams.get('pft') || undefined;
      const _preferred = searchParams.get('preferred_airlines') || undefined;
      const _directOnly = searchParams.get('direct_only') === '1';
      const _connectingOnly = searchParams.get('connecting_only') === '1';

      // Unified endpoint — Tripjack + Riya + Offline
      const response = await axios.get(`${API}/flights/search-all`, {
        params: {
          from_airport: fromAirport,
          to_airport: toAirport,
          date: departureDate,
          return_date: returnDate,
          adults: adults,
          children: children,
          infants: infants,
          class_type: travelClass,
          include_tripjack: true,
          include_offline: true,
          include_riya: true,
          ...(_pft ? { pft: _pft } : {}),
          ...(_preferred ? { preferred_airlines: _preferred } : {}),
          ...(_directOnly ? { direct_only: true } : {}),
          ...(_connectingOnly ? { connecting_only: true } : {}),
          // Mini-Sprint 1 · markup-engine context
          user_type: agentSession?.id ? 'agent' : 'b2c',
          ...(agentSession?.id ? { agent_id: agentSession.id } : {}),
        },
      });

      console.log('International RT API response:', response.data);

      // COMBO pair bundles (currently Tripjack-only; Riya serves per-leg pairs via /search-all)
      const comboRaw = response.data?.tripjack_combo_raw || [];
      const riyaOnward = response.data?.riya_onward_flights || [];
      const riyaReturn = response.data?.riya_return_flights || [];
      // Sprint-5 · Intl RT offline merge (I2). Backend returns offline pair
      // inventory under `offline_combo` (or per-leg under `offline_flights`).
      // Previously we requested `include_offline: true` but never read it,
      // hiding offline returns from B2C.
      const offlineCombos = response.data?.offline_combo
                         || response.data?.offline_flights
                         || [];
      const supplierErrors = response.data?.supplier_errors || {};

      const collected = [];
      if (comboRaw.length > 0) {
        collected.push(...transformComboFlights(comboRaw));
      }
      if (riyaOnward.length > 0 && riyaReturn.length > 0) {
        collected.push(...buildRiyaComboPairs(riyaOnward, riyaReturn));
      }
      // Offline rows ship in a near-final shape — preserve `source: 'offline'`
      // so downstream fare-rules + booking flows route them correctly.
      if (Array.isArray(offlineCombos) && offlineCombos.length > 0) {
        collected.push(
          ...offlineCombos.map((row, idx) => ({
            ...row,
            id: row.id || `offline-combo-${idx}`,
            source: 'offline',
          }))
        );
      }

      if (collected.length > 0) {
        console.log(
          `Loaded ${collected.length} Intl RT pairs `
          + `(Tripjack=${comboRaw.length}, Riya=${riyaOnward.length + riyaReturn.length}, Offline=${offlineCombos.length})`
        );
        setFlights(collected);
        setSupplierErrors({});
      } else {
        console.log('No COMBO flights in response — supplier_errors:', supplierErrors);
        setFlights([]);
        setSupplierErrors(supplierErrors);
      }
    } catch (err) {
      console.error('Flight search error:', err);
      console.error('Error details:', err.response?.data);
      setError('Failed to load flights. Please try again.');
      setFlights([]);
    } finally {
      setLoading(false);
    }
  };

  // Synthesise COMBO-style pair cards from Riya onward + return flight lists.
  // Pairs onward+return that share the SAME `riyaComboGroupId` — Riya's INTL
  // RT search returns combo halves; pricing only succeeds when both halves
  // come from the same combo group (otherwise Riya rejects with `(&6) Invalid
  // Price Itinerary count`).
  const buildRiyaComboPairs = (onwardList, returnList) => {
    const pairs = [];
    // Index returns by combo-group id for O(1) pairing
    const returnsByGroup = new Map();
    for (const r of returnList || []) {
      const gid = r.riyaComboGroupId;
      if (!gid) continue;
      if (!returnsByGroup.has(gid)) returnsByGroup.set(gid, []);
      returnsByGroup.get(gid).push(r);
    }
    for (const o of onwardList || []) {
      const gid = o.riyaComboGroupId;
      if (!gid) continue;
      const matchingReturns = returnsByGroup.get(gid) || [];
      for (const r of matchingReturns) {
        const depOnwardHHMM = (o.departureTime || '').split('T')[1]?.substring(0, 5) || '00:00';
        const arrOnwardHHMM = (o.arrivalTime || '').split('T')[1]?.substring(0, 5) || '00:00';
        const depReturnHHMM = (r.departureTime || '').split('T')[1]?.substring(0, 5) || '00:00';
        const arrReturnHHMM = (r.arrivalTime || '').split('T')[1]?.substring(0, 5) || '00:00';
        const totalPrice = (o.price || 0) + (r.price || 0);
        const totalBase = (o.baseFare || 0) + (r.baseFare || 0);
        const totalTax = (o.tax || 0) + (r.tax || 0);
        pairs.push({
          id: `riya-combo-${o.id}-${r.id}`,
          priceId: `riya-combo-${o.priceId}-${r.priceId}`,
          source: 'riya',
          // top-level fields used by filters
          stops: o.stops || 'Non Stop',
          airline: o.airline,
          airlineCode: o.airlineCode,
          flightNumber: o.flightNumber,
          departureTime: depOnwardHHMM,
          arrivalTime: arrOnwardHHMM,
          // onward
          onwardAirline: o.airline,
          onwardAirlineCode: o.airlineCode,
          onwardFlightNumber: o.flightNumber,
          onwardDepartureTime: depOnwardHHMM,
          onwardArrivalTime: arrOnwardHHMM,
          onwardDepartureDateTime: o.departureTime,
          onwardArrivalDateTime: o.arrivalTime,
          onwardDuration: o.duration,
          onwardStops: o.stops,
          onwardSegments: o.segments || [],
          // return
          returnAirline: r.airline,
          returnAirlineCode: r.airlineCode,
          returnFlightNumber: r.flightNumber,
          returnDepartureTime: depReturnHHMM,
          returnArrivalTime: arrReturnHHMM,
          returnDepartureDateTime: r.departureTime,
          returnArrivalDateTime: r.arrivalTime,
          returnDuration: r.duration,
          returnStops: r.stops,
          returnSegments: r.segments || [],
          // shared
          price: totalPrice,
          baseFare: totalBase,
          taxesAndFees: totalTax,
          class: o.classOfBooking || 'Economy',
          baggage: o.baggage || '15 Kg',
          cabinBaggage: o.cabinBaggage || '7 Kg',
          refundableType: o.refundable ? 1 : 0,
          seatsLeft: Math.min(o.seatsLeft || 9, r.seatsLeft || 9),
          mealIncluded: !!o.mealIncluded,
          // Riya-specific fields needed by the booking flow on BOTH legs
          // (Pricing/Book reads these from each leg's flightData).
          // Onward
          onwardRiyaTrackId: o.riyaTrackId,
          onwardRiyaFlightId: o.riyaFlightId,
          onwardRiyaBaseAmount: o.riyaBaseAmount,
          onwardRiyaGrossAmount: o.riyaGrossAmount,
          onwardRiyaSearchedAt: o.riyaSearchedAt,
          onwardRiyaItinRef: o.riyaItinRef,
          onwardRiyaSegRef: o.riyaSegRef,
          onwardRiyaReferenceToken: o.riyaReferenceToken,
          onwardRiyaCombo: o.riyaCombo,
          onwardRiyaComboGroupId: o.riyaComboGroupId,
          // Return
          returnRiyaTrackId: r.riyaTrackId,
          returnRiyaFlightId: r.riyaFlightId,
          returnRiyaBaseAmount: r.riyaBaseAmount,
          returnRiyaGrossAmount: r.riyaGrossAmount,
          returnRiyaSearchedAt: r.riyaSearchedAt,
          returnRiyaItinRef: r.riyaItinRef,
          returnRiyaSegRef: r.riyaSegRef,
          returnRiyaReferenceToken: r.riyaReferenceToken,
          returnRiyaCombo: r.riyaCombo,
          returnRiyaComboGroupId: r.riyaComboGroupId,
          // top-level Riya identifier (combos share the same TrackId pre-Pricing)
          riyaTrackId: o.riyaTrackId || r.riyaTrackId,
          riyaSearchedAt: o.riyaSearchedAt || r.riyaSearchedAt,
          riyaCombo: !!(o.riyaCombo && r.riyaCombo),
          riyaComboGroupId: o.riyaComboGroupId || r.riyaComboGroupId,
          rawOnward: o,
          rawReturn: r,
        });
      }
    }
    return pairs.sort((a, b) => a.price - b.price);
  };

  // Transform COMBO flights to display format and group by unique flight combination
  const transformComboFlights = (comboFlights) => {
    const flightMap = new Map();
    
    comboFlights.forEach((combo, index) => {
      const segments = combo.sI || [];
      
      // Split segments into onward and return
      // Onward: Origin → Destination
      // Return: Destination → Origin
      
      let splitIndex = Math.floor(segments.length / 2); // Default to middle
      
      // Better logic: Find where onward journey ends (when we reach final destination)
      // Onward ends at the segment where arrival airport matches the final destination
      for (let i = 0; i < segments.length; i++) {
        const arrivalCode = segments[i].aa?.code;
        const nextDepartureCode = segments[i + 1]?.da?.code;
        
        // If this segment arrives at destination AND next segment departs from destination
        // OR if this segment arrives at destination and is not followed by another segment to same destination
        if (arrivalCode === toCode) {
          // Check if next segment continues from destination (indicating start of return)
          if (i + 1 < segments.length && nextDepartureCode !== fromCode) {
            // Next segment exists and doesn't depart from origin
            // This means we're still at destination, check if next is heading back
            const nextArrival = segments[i + 1]?.aa?.code;
            if (nextArrival === fromCode || nextArrival === toCode || i + 1 === segments.length - 1) {
              // Next segment is either going back to origin or is the last segment
              splitIndex = i + 1;
              break;
            }
          } else {
            // This is the last onward segment
            splitIndex = i + 1;
            break;
          }
        }
      }
      
      const onwardSegments = segments.slice(0, splitIndex);
      const returnSegments = segments.slice(splitIndex);
      
      // Create unique key based on flight numbers and times
      const onwardKey = onwardSegments.map(s => `${s.fD?.fN}-${s.dt}`).join('|');
      const returnKey = returnSegments.map(s => `${s.fD?.fN}-${s.dt}`).join('|');
      const uniqueKey = `${onwardKey}__${returnKey}`;
      
      // Calculate onward duration (including layovers)
      const onwardDuration = onwardSegments.reduce((sum, seg, idx) => {
        let time = seg.duration || 0;
        if (idx < onwardSegments.length - 1 && seg.cT) {
          time += seg.cT;
        }
        return sum + time;
      }, 0);
      
      // Calculate return duration (including layovers)
      const returnDuration = returnSegments.reduce((sum, seg, idx) => {
        let time = seg.duration || 0;
        if (idx < returnSegments.length - 1 && seg.cT) {
          time += seg.cT;
        }
        return sum + time;
      }, 0);
      
      // Get fare options
      const fareOptions = (combo.totalPriceList || []).map(priceInfo => {
        const adultFare = priceInfo.fd?.ADULT || {};
        
        return {
          priceId: priceInfo.id,
          price: Math.round(adultFare.fC?.TF || 0),
          baseFare: Math.round(adultFare.fC?.BF || 0),
          taxes: Math.round(adultFare.fC?.TAF || 0),
          baggage: adultFare.bI?.iB || '15 Kg',
          cabinBaggage: adultFare.bI?.cB || '7 Kg',
          seatsLeft: adultFare.sR || null,
          mealIncluded: adultFare.mI || false,
          refundableType: adultFare.rT, // 0=Non-refundable, 1=Refundable, 2=Partially refundable
          fareType: publicFareTypeLabel(priceInfo.fareIdentifier),
          classOfBooking: adultFare.cB || 'Economy',
          tripBaggageBySegmentId: tripBaggageBySegmentFromPriceInfo(priceInfo) || undefined,
        };
      });
      
      // If we've seen this flight combination before, just add fare options
      if (flightMap.has(uniqueKey)) {
        const existingFlight = flightMap.get(uniqueKey);
        existingFlight.fareOptions = [...existingFlight.fareOptions, ...fareOptions];
        // Update to cheapest price
        const allPrices = existingFlight.fareOptions.map(f => f.price);
        const minPrice = Math.min(...allPrices);
        const cheapestFare = existingFlight.fareOptions.find(f => f.price === minPrice);
        existingFlight.price = cheapestFare.price;
        existingFlight.baggage = cheapestFare.baggage;
        existingFlight.cabinBaggage = cheapestFare.cabinBaggage;
        existingFlight.isRefundable = cheapestFare.isRefundable;
        existingFlight.priceId = cheapestFare.priceId;
        existingFlight.tripBaggageBySegmentId = cheapestFare.tripBaggageBySegmentId;
      } else {
        // New unique flight - create entry
        const primaryFare = fareOptions[0] || {};
        
        // Get airline info from first segment
        const firstSegment = segments[0] || {};
        const airlineCode = firstSegment.fD?.aI?.code || 'XX';
        const airlineName = firstSegment.fD?.aI?.name || 'Unknown Airline';
        
        // Calculate total stops
        const totalStops = segments.length > 2 ? segments.length - 2 : 0;
        
        const flightData = {
          id: uniqueKey,
          priceId: primaryFare.priceId,
          // ⚠️ source MUST be stamped so agent-booking flow routes to
          // /api/tripjack/book instead of falling through to the offline
          // path. Missing this in transformComboFlights caused booking
          // ACF4949101281 (Kuwait Airways BOM→DXB) to be created as
          // source=offline — agent wallet debited, but NO Tripjack /book
          // call made.
          source: 'tripjack',
          airline: airlineName,
          airlineCode: airlineCode,
          
          // Onward journey info
          onwardDepartureTime: onwardSegments[0]?.dt?.split('T')[1]?.substring(0, 5) || 'N/A',
          onwardArrivalTime: onwardSegments[onwardSegments.length - 1]?.at?.split('T')[1]?.substring(0, 5) || 'N/A',
          onwardDuration: `${Math.floor(onwardDuration / 60)}h ${onwardDuration % 60}m`,
          onwardStops: onwardSegments.length > 1 ? `${onwardSegments.length - 1} Stop${onwardSegments.length > 2 ? 's' : ''}` : 'Non Stop',
          
          // Return journey info
          returnDepartureTime: returnSegments[0]?.dt?.split('T')[1]?.substring(0, 5) || 'N/A',
          returnArrivalTime: returnSegments[returnSegments.length - 1]?.at?.split('T')[1]?.substring(0, 5) || 'N/A',
          returnDuration: `${Math.floor(returnDuration / 60)}h ${returnDuration % 60}m`,
          returnStops: returnSegments.length > 1 ? `${returnSegments.length - 1} Stop${returnSegments.length > 2 ? 's' : ''}` : 'Non Stop',
          
          from: fromCode,
          to: toCode,
          fromCity: onwardSegments[0]?.da?.city || fromCode,
          toCity: onwardSegments[onwardSegments.length - 1]?.aa?.city || toCode,
          
          stops: totalStops > 0 ? `${totalStops} Stop${totalStops > 1 ? 's' : ''}` : 'Non Stop',
          price: primaryFare.price,
          originalPrice: primaryFare.price,
          baseFare: primaryFare.baseFare,
          taxes: primaryFare.taxes,
          baggage: primaryFare.baggage,
          cabinBaggage: primaryFare.cabinBaggage,
          seatsLeft: primaryFare.seatsLeft,
          mealIncluded: primaryFare.mealIncluded,
          refundableType: primaryFare.refundableType, // 0=Non-refundable, 1=Refundable, 2=Partially refundable
          fareType: primaryFare.fareType,
          fareOptions: fareOptions,
          tripBaggageBySegmentId: primaryFare.tripBaggageBySegmentId,
          
          segments: segments,
          onwardSegments: onwardSegments,
          returnSegments: returnSegments,
          rawData: combo
        };
        
        flightMap.set(uniqueKey, flightData);
      }
    });
    
    return Array.from(flightMap.values());
  };

  // Fetch fare rules — keyed by (flightId, priceId) so per-fare rules are cached separately
  const fareRuleKey = (flightId, priceId) => `${flightId}__${priceId || ''}`;
  // Sprint-5 · Supplier-aware fare-rules fetch (I1).
  const fetchFareRules = async (priceId, flightId, flight = null) => {
    const key = fareRuleKey(flightId, priceId);
    if (fareRules[key]) return;

    setLoadingFareRules(prev => ({ ...prev, [key]: true }));

    try {
      const fl = flight || flights.find(f => f.id === flightId) || null;
      const response = await fetchFareRulesForFlight(fl || { source: 'tripjack' }, priceId);
      if (response.data) {
        setFareRules(prev => ({ ...prev, [key]: response.data }));
      }
    } catch (error) {
      console.error('Error fetching fare rules:', error);
      setFareRules(prev => ({
        ...prev,
        [key]: { error: 'Fare rules are temporarily unavailable. Please check the airline website for cancellation and change policies.' }
      }));
    } finally {
      setLoadingFareRules(prev => ({ ...prev, [key]: false }));
    }
  };

  const fromAirportInfo = airports.find((a) => a.code === fromCode);
  const toAirportInfo = airports.find((a) => a.code === toCode);

  // Extract unique airlines
  const uniqueAirlines = useMemo(() => {
    const airlineMap = new Map();
    flights.forEach(flight => {
      if (!airlineMap.has(flight.airlineCode)) {
        airlineMap.set(flight.airlineCode, {
          code: flight.airlineCode,
          name: flight.airline,
          count: 1
        });
      } else {
        airlineMap.get(flight.airlineCode).count++;
      }
    });
    return Array.from(airlineMap.values());
  }, [flights]);

  // Calculate cheapest price
  const cheapestPrice = useMemo(() => {
    if (flights.length === 0) return 0;
    return Math.min(...flights.map(f => f.price));
  }, [flights]);

  // Calculate stop counts
  const stopCounts = useMemo(() => {
    return {
      nonStop: flights.filter(f => f.stops === 'Non Stop').length,
      oneStop: flights.filter(f => f.stops === '1 Stop' || f.stops === '2 Stops').length,
      twoPlus: flights.filter(f => (f.stops || '').includes('3') || (f.stops || '').includes('4')).length
    };
  }, [flights]);

  // Fetch airline tail logos (Logostream, cached via utils/airlineLogoCache)
  useEffect(() => {
    if (uniqueAirlines.length > 0) {
      const codes = uniqueAirlines.map(a => a.code);
      fetchAirlineLogos(codes)
        .then(data => setAirlineLogos(prev => ({ ...prev, ...data })))
        .catch(err => console.error('Airline logo fetch error:', err));
    }
  }, [uniqueAirlines]);

  // Helper function to check if a time falls in a time slot
  const isTimeInSlot = (time, slotId) => {
    const hour = parseInt(time.split(':')[0]);
    if (slotId === 'earlyMorning') return hour >= 0 && hour < 6;
    if (slotId === 'morning') return hour >= 6 && hour < 12;
    if (slotId === 'afternoon') return hour >= 12 && hour < 18;
    if (slotId === 'evening') return hour >= 18 && hour < 24;
    return false;
  };

  // Filter and sort flights
  const filteredFlights = useMemo(() => {
    let filteredList = [...flights];
    
    // Stop filter
    if (selectedStops.length > 0) {
      filteredList = filteredList.filter((f) => {
        const s = f.stops || '';
        if (selectedStops.includes('nonStop') && s === 'Non Stop') return true;
        if (selectedStops.includes('1stop') && (s.includes('1 Stop') || s.includes('2 Stop'))) return true;
        if (selectedStops.includes('2stop') && (s.includes('3 Stop') || s.includes('4 Stop'))) return true;
        return false;
      });
    }
    
    // Airline filter
    if (selectedAirlines.length > 0) {
      filteredList = filteredList.filter((f) => selectedAirlines.includes(f.airlineCode));
    }

    // Filter by departure time (onward)
    if (selectedDepartureTimes.length > 0) {
      filteredList = filteredList.filter((f) => 
        selectedDepartureTimes.some(slot => isTimeInSlot(f.onwardDepartureTime, slot))
      );
    }

    // Filter by arrival time (return)
    if (selectedArrivalTimes.length > 0) {
      filteredList = filteredList.filter((f) => 
        selectedArrivalTimes.some(slot => isTimeInSlot(f.returnArrivalTime, slot))
      );
    }
    
    // Price filter
    filteredList = filteredList.filter((f) => f.price >= priceRange[0] && f.price <= priceRange[1]);
    
    // Sorting
    if (sortBy === 'price') {
      filteredList.sort((a, b) => a.price - b.price);
    } else if (sortBy === 'duration') {
      // Sort by total duration (onward + return)
      filteredList.sort((a, b) => {
        const [aOH, aOM] = a.onwardDuration.split('h ').map(s => parseInt(s));
        const [aRH, aRM] = a.returnDuration.split('h ').map(s => parseInt(s));
        const aTotalMins = (aOH * 60 + aOM) + (aRH * 60 + aRM);
        
        const [bOH, bOM] = b.onwardDuration.split('h ').map(s => parseInt(s));
        const [bRH, bRM] = b.returnDuration.split('h ').map(s => parseInt(s));
        const bTotalMins = (bOH * 60 + bOM) + (bRH * 60 + bRM);
        
        return aTotalMins - bTotalMins;
      });
    } else if (sortBy === 'departure') {
      filteredList.sort((a, b) => a.onwardDepartureTime.localeCompare(b.onwardDepartureTime));
    }
    
    return filteredList;
  }, [flights, selectedStops, selectedAirlines, selectedDepartureTimes, selectedArrivalTimes, priceRange, sortBy]);

  // Paginated flights for performance (show 20 initially)
  const paginatedFlights = useMemo(() => {
    return filteredFlights.slice(0, displayCount);
  }, [filteredFlights, displayCount]);

  const handleLoadMore = () => {
    setDisplayCount(prev => prev + ITEMS_PER_PAGE);
  };

  // Infinite scroll — mobile + desktop sentinels (each gets its own observer)
  const hasMoreFlights = displayCount < filteredFlights.length;
  const infiniteScrollRefMobile = useInfiniteScroll({
    hasMore: hasMoreFlights && !loading,
    onLoadMore: handleLoadMore,
  });
  const infiniteScrollRefDesktop = useInfiniteScroll({
    hasMore: hasMoreFlights && !loading,
    onLoadMore: handleLoadMore,
  });

  // Handle Modify Search
  const handleModifySearch = (values) => {
    // If values are passed, use them (from DesktopModifySearch)
    // Otherwise use current state (from mobile modify)
    if (values) {
      navigate(`/international-round-trip?from=${values.fromAirport}&to=${values.toAirport}&date=${values.departureDate}&return=${values.returnDate}&adults=${values.adults}&children=${values.children}&infants=${values.infants}&class=${values.travelClass}`);
    } else {
      navigate(`/international-round-trip?from=${fromCode}&to=${toCode}&date=${departureDate}&return=${returnDate}&adults=${adults}&children=${children}&infants=${infants}&class=${travelClass}`);
    }
    setShowModifySearch(false);
  };

  const toggleExpand = (flightId) => {
    if (expandedFlight === flightId) {
      setExpandedFlight(null);
      setActiveTab({});
    } else {
      setExpandedFlight(flightId);
      setActiveTab({ [flightId]: 'journey' });
      
      // Fetch fare rules when expanding — use currently selected fare's priceId
      const flight = flights.find(f => f.id === flightId);
      const activePid = selectedFares[flightId]?.priceId || flight?.priceId;
      if (activePid) {
        fetchFareRules(activePid, flightId);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50">
      <PullToRefresh onRefresh={fetchFlights}>
        {/* Mobile Header - Matching SearchResults Style */}
        <div className="md:hidden sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
          <div className="flex items-center gap-3 p-3">
            <button
              onClick={() => navigate('/')}
              className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center active:bg-gray-200 transition-colors"
              aria-label="Back to home"
            >
              <ArrowRight className="w-5 h-5 text-[#1B375B] rotate-180" />
            </button>
            
            <button
              onClick={() => setShowModifySearch(true)}
              className="flex-1 bg-gray-100 rounded-full px-4 py-3 active:bg-gray-200 transition-colors"
              style={{ minHeight: '48px' }}
            >
              <div className="text-center">
                <p className="font-bold text-[#1B375B] text-base">
                  {fromCode} ⇄ {toCode}
                </p>
                <p className="text-xs text-gray-500">
                  {departureDate ? new Date(departureDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short' }) : 'Date'} - {returnDate ? new Date(returnDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short' }) : 'Return'} • {adults + children + infants} Traveller{(adults + children + infants) > 1 ? 's' : ''} • {travelClass}
                </p>
              </div>
            </button>

            <button className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center active:bg-gray-200 transition-colors">
              <Users className="w-5 h-5 text-[#1B375B]" />
            </button>
          </div>
        </div>

        {/* Mobile Flight Cards - Matching SearchResults Style */}
        <div className="md:hidden p-3 space-y-3 pb-24">
          {/* Loading State - Skeleton */}
          {loading && (
            <div className="space-y-3">
              {[...Array(6)].map((_, i) => (
                <FlightSkeleton key={i} />
              ))}
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
              <p className="text-red-700 font-medium mb-2">{error}</p>
              <button
                onClick={fetchFlights}
                className="mt-3 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          )}

          {/* No Flights Found */}
          {!loading && !error && filteredFlights.length === 0 && (
            <div className="bg-gray-50 rounded-xl p-8 text-center" data-testid="zero-results">
              <Plane className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 font-medium">No flights found</p>
              {Object.keys(supplierErrors).length > 0 ? (
                <div className="mt-3 space-y-1">
                  {Object.entries(supplierErrors).map(([supplier, err]) => (
                    <p key={supplier} className="text-xs text-orange-600">
                      <span className="font-semibold capitalize">{supplier}:</span> {String(err).slice(0, 200)}
                    </p>
                  ))}
                  <button
                    onClick={fetchFlights}
                    data-testid="retry-flights-btn"
                    className="mt-3 px-5 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"
                  >
                    Retry search
                  </button>
                </div>
              ) : (
                <p className="text-sm text-gray-500 mt-1">Try adjusting your search criteria</p>
              )}
            </div>
          )}

          {/* Flight Results - Mobile */}
          {!loading && !error && paginatedFlights.map((flight) => (
            <ComboFlightCard
              key={flight.id}
              flight={flight}
              airlineLogos={airlineLogos}
              cheapestPrice={cheapestPrice}
              isExpanded={expandedFlight === flight.id}
              onToggleExpand={() => toggleExpand(flight.id)}
              selectedFare={selectedFares[flight.id]}
              onFareSelect={(fare) => {
                setSelectedFares(prev => ({
                  ...prev,
                  [flight.id]: fare
                }));
                // If Fare Rules tab is open, fetch rules for the newly-selected fare
                if (activeTab[flight.id] === 'fareRules' && fare.priceId) {
                  fetchFareRules(fare.priceId, flight.id);
                }
              }}
              onBook={() => {
                const fareToBook = selectedFares[flight.id] || flight;
                // Shared helper — see utils/fareSelection.js for the field-override contract.
                const baseBookingData = buildSelectedFlightPayload(flight, fareToBook);
                const bookingData = {
                  ...baseBookingData,
                  source: fareToBook.source || flight.source || 'tripjack',
                };
                localStorage.setItem('selectedFlight', JSON.stringify(bookingData));
                localStorage.setItem('searchTimestamp', Date.now().toString());
                navigate(`/booking?priceId=${fareToBook.priceId}&type=combo&adults=${adults}&children=${children}&infants=${infants}`);
              }}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              fareRulesData={fareRules[fareRuleKey(flight.id, (selectedFares[flight.id]?.priceId || flight.priceId))]}
              loadingFareRulesData={loadingFareRules[fareRuleKey(flight.id, (selectedFares[flight.id]?.priceId || flight.priceId))]}
              onFareRulesOpen={() => fetchFareRules(selectedFares[flight.id]?.priceId || flight.priceId, flight.id)}
            />
          ))}

          {/* Load More Button + Infinite Scroll Sentinel - Mobile */}
          {!loading && hasMoreFlights && (
            <div className="flex flex-col items-center py-4 gap-2">
              <button
                onClick={handleLoadMore}
                className="px-6 py-3 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-colors flex items-center gap-2"
                data-testid="load-more-intl-mobile-btn"
              >
                Load More Flights
                <ChevronDown className="w-4 h-4" />
              </button>
              <div ref={infiniteScrollRefMobile} className="h-1 w-full" aria-hidden="true" />
              <p className="text-xs text-gray-400 animate-pulse">Loading more as you scroll...</p>
            </div>
          )}
        </div>

        {/* Desktop Modify Search Component */}
        <div className="hidden md:block">
          <DesktopModifySearch
            showModifySearch={showModifySearch}
            setShowModifySearch={setShowModifySearch}
            tripType="roundTrip"
            setTripType={() => {}}
            fromAirport={fromCode}
            setFromAirport={() => {}}
            toAirport={toCode}
            setToAirport={() => {}}
            departureDate={departureDate}
            setDepartureDate={() => {}}
            returnDate={returnDate}
            setReturnDate={() => {}}
            adults={adults}
            setAdults={() => {}}
            children={children}
            setChildren={() => {}}
            infants={infants}
            setInfants={() => {}}
            travelClass={travelClass}
            setTravelClass={() => {}}
            onSearch={handleModifySearch}
            showTripTypeSelector={false}
          />

          {/* Main Desktop Content */}
          <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex gap-5">
            {/* Desktop Filter Sidebar */}
            <div className="hidden lg:block w-[260px] flex-shrink-0">
              <FilterSidebar
                selectedStops={selectedStops}
                setSelectedStops={setSelectedStops}
                selectedAirlines={selectedAirlines}
                setSelectedAirlines={setSelectedAirlines}
                selectedDepartureTimes={selectedDepartureTimes}
                setSelectedDepartureTimes={setSelectedDepartureTimes}
                selectedArrivalTimes={selectedArrivalTimes}
                setSelectedArrivalTimes={setSelectedArrivalTimes}
                priceRange={priceRange}
                setPriceRange={setPriceRange}
                stopCounts={stopCounts}
                uniqueAirlines={uniqueAirlines}
                airlineLogos={airlineLogos}
              />
            </div>

            {/* Flight Results */}
            <div className="flex-1">
              {/* Results Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-[#1B375B]">
                    {loading ? 'Searching...' : `${filteredFlights.length} Round-Trip Options`}
                  </h2>
                  {cheapestPrice > 0 && (
                    <p className="text-sm text-gray-600 mt-1">
                      Starting from <span className="font-bold text-teal-700">₹{cheapestPrice.toLocaleString()}</span>
                    </p>
                  )}
                </div>


                {/* Sort Buttons */}
                <SortButtons sortBy={sortBy} setSortBy={setSortBy} />

              </div>

              {/* Mobile Filter/Sort Bar */}
              <div className="lg:hidden flex gap-2 mb-4">
                <button
                  onClick={() => setShowFilters(true)}
                  className="flex-1 py-3 bg-white rounded-xl shadow-sm border border-gray-200 flex items-center justify-center gap-2 font-semibold text-gray-700"
                >
                  <Filter className="w-4 h-4" />
                  Filters
                  {(selectedStops.length + selectedAirlines.length) > 0 && (
                    <span className="bg-teal-600 text-white text-xs px-2 py-0.5 rounded-full">
                      {selectedStops.length + selectedAirlines.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => {
                    const sorts = ['price', 'duration', 'departure'];
                    const currentIdx = sorts.indexOf(sortBy);
                    setSortBy(sorts[(currentIdx + 1) % sorts.length]);
                  }}
                  className="flex-1 py-3 bg-white rounded-xl shadow-sm border border-gray-200 flex items-center justify-center gap-2 font-semibold text-gray-700"
                >
                  <ArrowUpDown className="w-4 h-4" />
                  Sort: {sortBy === 'price' ? 'Price' : sortBy === 'duration' ? 'Duration' : 'Time'}
                </button>
              </div>

              {/* Loading State - Desktop Skeleton */}
              {loading && (
                <div className="space-y-4">
                  {[...Array(6)].map((_, i) => (
                    <FlightSkeleton key={i} />
                  ))}
                </div>
              )}

              {/* Error State */}
              {error && !loading && (
                <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
                  <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
                  <p className="text-red-700 font-semibold">{error}</p>
                  <button
                    onClick={fetchFlights}
                    className="mt-4 px-6 py-2 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              )}

              {/* No Results */}
              {!loading && !error && filteredFlights.length === 0 && flights.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6 text-center">
                  <Filter className="w-12 h-12 text-yellow-600 mx-auto mb-3" />
                  <p className="text-yellow-800 font-semibold mb-2">No flights match your filters</p>
                  <button
                    onClick={() => {
                      setSelectedStops([]);
                      setSelectedAirlines([]);
                      setPriceRange([0, 300000]);
                    }}
                    className="mt-2 px-6 py-2 bg-yellow-600 text-white rounded-xl font-semibold hover:bg-yellow-700 transition-colors"
                  >
                    Clear Filters
                  </button>
                </div>
              )}

              {/* Flight Cards */}
              {!loading && filteredFlights.length > 0 && (
                <div className="space-y-4">
                  {paginatedFlights.map((flight) => (
                    <ComboFlightCard
                      key={flight.id}
                      flight={flight}
                      airlineLogos={airlineLogos}
                      cheapestPrice={cheapestPrice}
                      isExpanded={expandedFlight === flight.id}
                      onToggleExpand={() => toggleExpand(flight.id)}
                      selectedFare={selectedFares[flight.id]}
                      onFareSelect={(fare) => {
                        setSelectedFares(prev => ({
                          ...prev,
                          [flight.id]: fare
                        }));
                        if (activeTab[flight.id] === 'fareRules' && fare.priceId) {
                          fetchFareRules(fare.priceId, flight.id);
                        }
                      }}
                      onBook={() => {
                        // Use selected fare if available, otherwise use default flight data
                        const fareToBook = selectedFares[flight.id] || flight;
                        
                        // Store COMBO flight data for booking
                        const bookingData = {
                          type: 'combo',
                          source: fareToBook.source || flight.source || 'tripjack',
                          flight: buildSelectedFlightPayload(flight, fareToBook),
                          route: {
                            from: fromCode,
                            to: toCode,
                            departureDate: departureDate,
                            returnDate: returnDate
                          },
                          passengers: {
                            adults,
                            children,
                            infants
                          },
                          travelClass
                        };
                        
                        localStorage.setItem('selectedFlight', JSON.stringify(bookingData));
                        navigate(`/booking?priceId=${fareToBook.priceId}&type=combo&adults=${adults}&children=${children}&infants=${infants}`);
                      }}
                      activeTab={activeTab}
                      setActiveTab={setActiveTab}
                      fareRulesData={fareRules[fareRuleKey(flight.id, (selectedFares[flight.id]?.priceId || flight.priceId))]}
                      loadingFareRulesData={loadingFareRules[fareRuleKey(flight.id, (selectedFares[flight.id]?.priceId || flight.priceId))]}
                      onFareRulesOpen={() => fetchFareRules(selectedFares[flight.id]?.priceId || flight.priceId, flight.id)}
                    />
                  ))}

                  {/* Load More Button + Infinite Scroll Sentinel - Desktop */}
                  {hasMoreFlights && (
                    <div className="flex flex-col items-center py-4 gap-2">
                      <button
                        onClick={handleLoadMore}
                        className="px-6 py-3 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-colors flex items-center gap-2"
                        data-testid="load-more-intl-desktop-btn"
                      >
                        Load More Flights
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <div ref={infiniteScrollRefDesktop} className="h-1 w-full" aria-hidden="true" />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        </div>

        {/* Mobile Filter Bottom Sheet */}

        {/* Mobile Filter Sheet */}
        <MobileFilterSheet
          isOpen={showFilters}
          onClose={() => setShowFilters(false)}
          selectedStops={selectedStops}
          setSelectedStops={setSelectedStops}
          selectedAirlines={selectedAirlines}
          setSelectedAirlines={setSelectedAirlines}
          priceRange={priceRange}
          setPriceRange={setPriceRange}
          stopCounts={stopCounts}
          uniqueAirlines={uniqueAirlines}
          airlineLogos={airlineLogos}
        />

        {/* Mobile Modify Search Sheet - Shows on mobile, hidden on desktop */}
        <MobileModifySearch
          isOpen={showModifySearch}
          onClose={() => setShowModifySearch(false)}
          tripType="roundTrip"
          fromAirport={fromAirport}
          setFromAirport={setFromAirport}
          toAirport={toAirport}
          setToAirport={setToAirport}
          departureDate={departureDate}
          setDepartureDate={setDepartureDate}
          returnDate={returnDate}
          setReturnDate={setReturnDate}
          adults={adults}
          setAdults={setAdults}
          children={children}
          setChildren={setChildren}
          infants={infants}
          setInfants={setInfants}
          travelClass={travelClass}
          setTravelClass={setTravelClass}
          onSearch={handleModifySearch}
        />

      </PullToRefresh>

      {/* Bottom Filter Menu - Mobile Only */}
      <div 
        className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.1)]"
        style={{ zIndex: 9999, position: 'fixed' }}
      >
        <div className="flex items-center justify-around py-3" style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}>
          <button
            onClick={() => setShowFilters(true)}
            className="flex flex-col items-center gap-1 px-3 py-2 active:bg-gray-100 rounded-lg transition-colors"
            style={{ minWidth: '60px', minHeight: '48px' }}
          >
            <Filter className="w-5 h-5 text-gray-600" />
            <span className="text-xs font-medium text-gray-700">FILTER</span>
          </button>

          <button
            onClick={() => {
              setSelectedStops(prev => prev.includes('nonStop') ? prev.filter(s => s !== 'nonStop') : [...prev, 'nonStop']);
            }}
            className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-colors ${
              selectedStops.includes('nonStop') ? 'bg-[#1B375B] text-white' : 'text-gray-600 active:bg-gray-100'
            }`}
            style={{ minWidth: '60px', minHeight: '48px' }}
          >
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
              selectedStops.includes('nonStop') ? 'border-white' : 'border-gray-600'
            }`}>
              <div className={`w-2 h-2 rounded-full ${selectedStops.includes('nonStop') ? 'bg-white' : ''}`} />
            </div>
            <span className="text-xs font-medium">NON-STOP</span>
          </button>

          <button
            onClick={() => setShowTimingSheet(true)}
            className="flex flex-col items-center gap-1 px-3 py-2 active:bg-gray-100 rounded-lg transition-colors relative"
            style={{ minWidth: '60px', minHeight: '48px' }}
          >
            <Clock className="w-5 h-5 text-gray-600" />
            <span className="text-xs font-medium text-gray-700">TIME</span>
            {(selectedDepartureTimes.length > 0 || selectedArrivalTimes.length > 0) && (
              <div className="absolute top-1 right-1 w-2 h-2 rounded-full bg-teal-500" />
            )}
          </button>

          <button
            onClick={() => setShowFilters(true)}
            className="flex flex-col items-center gap-1 px-3 py-2 active:bg-gray-100 rounded-lg transition-colors"
            style={{ minWidth: '60px', minHeight: '48px' }}
          >
            <Plane className="w-5 h-5 text-gray-600" />
            <span className="text-xs font-medium text-gray-700">AIRLINE</span>
          </button>

          <button
            onClick={() => setShowSortSheet(true)}
            className="flex flex-col items-center gap-1 px-3 py-2 active:bg-gray-100 rounded-lg transition-colors relative"
            style={{ minWidth: '60px', minHeight: '48px' }}
          >
            <ArrowUpDown className="w-5 h-5 text-gray-600" />
            <span className="text-xs font-medium text-gray-700">SORT</span>
            {sortBy !== 'price' && (
              <div className="absolute top-1 right-2 w-2 h-2 rounded-full bg-red-500" />
            )}
          </button>
        </div>
      </div>

      {/* Timing Filter Bottom Sheet - For Round Trip */}
      <BottomSheet isOpen={showTimingSheet} onClose={() => setShowTimingSheet(false)} title="Flight Times">
        <div className="p-4 space-y-4">
          {/* Onward Departure Time */}
          <div>
            <h4 className="font-bold text-[#1B375B] mb-3">Onward Departure</h4>
            <div className="space-y-2">
              {[
                { id: 'earlyMorning', label: 'Early Morning', time: '12am - 6am', icon: '🌙' },
                { id: 'morning', label: 'Morning', time: '6am - 12pm', icon: '🌅' },
                { id: 'afternoon', label: 'Afternoon', time: '12pm - 6pm', icon: '☀️' },
                { id: 'evening', label: 'Evening', time: '6pm - 12am', icon: '🌆' }
              ].map((slot) => (
                <button
                  key={slot.id}
                  onClick={() => {
                    setSelectedDepartureTimes(prev =>
                      prev.includes(slot.id)
                        ? prev.filter(t => t !== slot.id)
                        : [...prev, slot.id]
                    );
                  }}
                  className={`w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                    selectedDepartureTimes.includes(slot.id)
                      ? 'border-teal-600 bg-teal-50'
                      : 'border-gray-200 bg-white active:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{slot.icon}</span>
                    <div className="text-left">
                      <p className="font-semibold text-sm text-[#1B375B]">{slot.label}</p>
                      <p className="text-xs text-gray-500">{slot.time}</p>
                    </div>
                  </div>
                  {selectedDepartureTimes.includes(slot.id) && (
                    <div className="w-5 h-5 bg-teal-600 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Return Arrival Time */}
          <div>
            <h4 className="font-bold text-[#1B375B] mb-3">Return Arrival</h4>
            <div className="space-y-2">
              {[
                { id: 'earlyMorning', label: 'Early Morning', time: '12am - 6am', icon: '🌙' },
                { id: 'morning', label: 'Morning', time: '6am - 12pm', icon: '🌅' },
                { id: 'afternoon', label: 'Afternoon', time: '12pm - 6pm', icon: '☀️' },
                { id: 'evening', label: 'Evening', time: '6pm - 12am', icon: '🌆' }
              ].map((slot) => (
                <button
                  key={slot.id}
                  onClick={() => {
                    setSelectedArrivalTimes(prev =>
                      prev.includes(slot.id)
                        ? prev.filter(t => t !== slot.id)
                        : [...prev, slot.id]
                    );
                  }}
                  className={`w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                    selectedArrivalTimes.includes(slot.id)
                      ? 'border-teal-600 bg-teal-50'
                      : 'border-gray-200 bg-white active:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{slot.icon}</span>
                    <div className="text-left">
                      <p className="font-semibold text-sm text-[#1B375B]">{slot.label}</p>
                      <p className="text-xs text-gray-500">{slot.time}</p>
                    </div>
                  </div>
                  {selectedArrivalTimes.includes(slot.id) && (
                    <div className="w-5 h-5 bg-teal-600 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={() => setShowTimingSheet(false)}
            className="w-full mt-4 bg-gradient-to-r from-[#659A9B] to-[#335E77] text-white font-bold py-3 rounded-lg"
          >
            Apply
          </button>
        </div>
      </BottomSheet>

      {/* Sort Options Bottom Sheet */}
      <BottomSheet isOpen={showSortSheet} onClose={() => setShowSortSheet(false)} title="Sort By">
        <div className="p-4 space-y-3">
          {[
            { id: 'price', label: 'Price', desc: 'Lowest to Highest', icon: '💰' },
            { id: 'duration', label: 'Duration', desc: 'Shortest First', icon: '⏱️' },
            { id: 'departure', label: 'Departure Time', desc: 'Earliest First', icon: '🛫' }
          ].map((option) => (
            <button
              key={option.id}
              onClick={() => {
                setSortBy(option.id);
                setShowSortSheet(false);
              }}
              className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                sortBy === option.id
                  ? 'border-teal-600 bg-teal-50'
                  : 'border-gray-200 bg-white active:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">{option.icon}</span>
                <div className="text-left">
                  <p className="font-bold text-[#1B375B]">{option.label}</p>
                  <p className="text-xs text-gray-500">{option.desc}</p>
                </div>
              </div>
              {sortBy === option.id && (
                <div className="w-6 h-6 bg-teal-600 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </button>
          ))}
        </div>
      </BottomSheet>

      {/* Footer - Hidden on mobile */}
      <div className="hidden md:block">
        <Footer />
      </div>
    </div>
  );
};

export default InternationalRoundTripResults;
